import { useMemo } from "react";
import { BEModulesClassNames } from "@utils/moduleList";
import { ForestManagementSections } from "./forestManagement";
import { fetchLandUseTypesHybrid } from "../moduleUtils";
import { ModuleSchema } from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";

/**
 * Custom hook that processes the forest management module schema to handle special requirements.
 * 
 * 
 * This hook performs two main operations:
 * 1. **Degradation Extraction**: Extracts the degradation submodule from mandatory submodules
 *    and filters it out, as degradation is handled separately in the UI. (to be able to show it after the disturbances section)
 * 2. **Dynamic Dropdown Population**: Updates the vegetation levels dropdown in the TYPE_OF_FOREST
 *    submodule with dynamic data fetched from the API (because we can't fetch it in the schemaFile having them filterd by activity/project climate and moisture)
 * 
 * @param moduleSchema - The original module schema containing all submodules and input groups
 * @param activity - Activity data used for fetching land use types
 * @param project - Project data used for fetching land use types  
 * @param forestContext - Forest context for updating forest types data
 * @param isReadOnly - Boolean flag to disable inputs when in read-only mode
 * 
 * @returns An object containing:
 *   - degradation: The extracted degradation submodule (for separate rendering)
 *   - processedModuleSchema: The modified schema with degradation filtered out and 
 *     vegetation levels dropdown populated with dynamic data
 * 
 * @example
 * ```typescript
 * const { degradation, processedModuleSchema } = useProcessedModuleSchema(
 *   moduleProps.moduleSchema,
 *   activity,
 *   project,
 *   forestContext,
 *   isReadOnly
 * );
 * ```
 */
export const useProcessedModuleSchema = (
  moduleSchema: ModuleSchema,
  activity: any,
  project: any,
  forestContext: any,
  isReadOnly: boolean
) => {
  return useMemo(() => {
    // Extract degradation submodule and filter it out from mandatory submodules
    const degradation = moduleSchema.mandatorySubmodules.find(
      (submodule) => submodule.name === ForestManagementSections.DEGRADATION
    );

    // Create vegetationLevels options function using class name
    const vegetationLevelsOptions = fetchLandUseTypesHybrid({
      className: BEModulesClassNames.ForestManagement, // Use class name instead of numeric ID
      activity,
      project
    });

    // Process mandatory submodules
    const processedMandatorySubmodules = moduleSchema.mandatorySubmodules
      .filter((submodule) => submodule.name !== ForestManagementSections.DEGRADATION)
      .map(submodule => {
        // Update TYPE_OF_FOREST submodule to include dynamic dropdownOptions
        if (submodule.name === ForestManagementSections.TYPE_OF_FOREST) {
          return {
            ...submodule,
            inputGroups: submodule.inputGroups.map(inputGroup => {
              if (inputGroup.type === FieldType.SELECT &&
                inputGroup.inputName === "parent.land_use_type_start") {
                return {
                  ...inputGroup,
                  dropdownOptions: async () => {
                    let lucTypesKit = await vegetationLevelsOptions();
                    if (forestContext) forestContext.setLucTypes(lucTypesKit.data);
                    return lucTypesKit;
                  },
                  disabled: isReadOnly,
                };
              }
              return inputGroup;
            })
          };
        }
        return submodule;
      });

    return {
      degradation,
      processedModuleSchema: {
        ...moduleSchema,
        mandatorySubmodules: processedMandatorySubmodules
      }
    };
  }, [moduleSchema, activity, project, isReadOnly]);
};